import ProfileImage, {
  type ProfileImageErrors,
  type ProfileImageGivingUserProfile,
} from '@brands/Components/ProfileImage/ProfileImage';
import ConfirmationModal from '@brands/Components/Modals/ConfirmationModal/ConfirmationModal';
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import useUnlockAccount from './useUnlockAccount';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { displayErrorDetails } from '@brands/Utils/displayError';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { Alert, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { IoAdd, IoChevronBack, IoChevronDown, IoEllipsisVertical } from 'react-icons/io5';
import { toast } from 'react-toastify';

import addNewUserStyles from '../../Dashboard/Collector/AddNewUser/addNewUser.module.scss';
import collectorShellStyles from '../../Dashboard/Collector/EditUser/collectorEditUser.module.scss';
import styles from '../Provider/styles.module.scss';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { resolveGuardianDisplayName } from '../../../Utils/guardianDisplay';

type ModernReviewProfileHeaderProps = {
  thisUser: UserProfile;
  setThisUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  userId: string;
  resetEmailSent: boolean;
  setResetPasswordConfirmation: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      userState: string;
    }>
  >;
  setChangeUserStatusFlag: React.Dispatch<React.SetStateAction<string>>;
  setCurrentAvatarURL: React.Dispatch<React.SetStateAction<string>>;
  setAvatarFile: React.Dispatch<React.SetStateAction<File | null>>;
  setAvatarPayload: React.Dispatch<React.SetStateAction<RegisterMediaPayload | null>>;
  setAvatarAction: React.Dispatch<React.SetStateAction<string>>;
  triggerProfileAvatar: (avatarUrl: string) => void;
  setImage: React.Dispatch<React.SetStateAction<string | undefined>>;
  image: string | undefined;
  errors: ProfileImageErrors;
  roleName: string;
  onBack?: () => void;
  onCreateCase?: () => void;
  className?: string;
};

const ModernReviewProfileHeader = ({
  thisUser,
  setThisUser,
  userId,
  resetEmailSent,
  setResetPasswordConfirmation,
  setChangeUserStatusFlag,
  setCurrentAvatarURL,
  setAvatarFile,
  setAvatarPayload,
  setAvatarAction,
  triggerProfileAvatar,
  setImage,
  image,
  errors,
  roleName,
  onBack,
  onCreateCase,
  className,
}: ModernReviewProfileHeaderProps): JSX.Element => {
  const navigate = useRoleNavigate();
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const { userInfo } = useAppSelector(selectAuth);
  const {
    showUnlockAccount,
    isUnlocking,
    unlockConfirmOpen,
    openUnlockConfirm,
    closeUnlockConfirm,
    handleUnlockAccount,
  } = useUnlockAccount(thisUser, setThisUser);

  const headerDisplayName = useMemo(() => {
    return (
      thisUser.full_name?.trim() ||
      [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name].filter(Boolean).join(' ').trim() ||
      '—'
    );
  }, [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name, thisUser.full_name]);

  const displayStatus = useMemo(() => {
    if ((thisUser as { deleted_at?: string })?.deleted_at) return 'Deactivated';
    return thisUser.status?.trim() || 'Active';
  }, [thisUser]);

  const statusPillClass =
    displayStatus === 'Active'
      ? collectorShellStyles.statusPillReadOnlyActive
      : displayStatus === 'Deactivated'
      ? collectorShellStyles.statusPillReadOnlyDeactivated
      : collectorShellStyles.statusPillReadOnlyOther;

  const statusOptions = useMemo(() => {
    if (
      thisUser.role.name === UserRoleName.Provider &&
      displayStatus !== 'Pending' &&
      displayStatus !== 'Deactivated'
    ) {
      return [
        { id: 'pending', name: 'Pending' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (thisUser.role.name === UserRoleName.Provider && displayStatus === 'Pending') {
      return [
        { id: 'active', name: 'Active' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (displayStatus !== 'Deactivated') {
      return [{ id: 'deactivated', name: 'Deactivated' }];
    }
    return [{ id: 'active', name: 'Active' }];
  }, [displayStatus, thisUser.role.name]);

  const isDependent = thisUser.role.name === UserRoleName.Dependent;

  const guardianName = useMemo(() => {
    return resolveGuardianDisplayName(thisUser);
  }, [thisUser.guardian_full_name, thisUser.guardian]);

  const canCreateCase =
    thisUser.role.name === UserRoleName.Patient || thisUser.role.name === UserRoleName.Dependent;

  const getInvitationDomain = (): string => {
    const organizations = thisUser.organizations || [];
    const org =
      organizations.find((organization) => organization.id !== 'dentistry-one') ??
      organizations.find((organization) => organization.id === 'dentistry-one');
    const currentDomain = window.location.hostname;
    const fallbackDomain =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;

    if (!org) return fallbackDomain;

    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;

    return divisionDomain || practiceDomain || fallbackDomain;
  };

  const inviteUser = async (): Promise<void> => {
    setIsInvitedUser(true);
    try {
      const updatedUser = await reinviteUserById(
        userId,
        { frontend_website: `https://${getInvitationDomain()}` },
        roleName,
      );
      setThisUser({ ...thisUser, status: updatedUser?.status, email: updatedUser?.email });
    } catch (error: unknown) {
      const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (responseMessage?.includes('User has no email') || responseMessage?.includes('12209-3')) {
        setDisplayEmailModal(true);
        return;
      }
      displayErrorDetails(error);
    } finally {
      setIsInvitedUser(false);
    }
  };

  const handleStatusClick = async (status: string): Promise<void> => {
    if (status === 'deactivated' || status === 'pending') {
      setChangeUserStatusFlag(status);
    } else {
      try {
        const userProfile = await activateUserById(Number(userId));
        setThisUser({ ...thisUser, status: userProfile?.status ?? thisUser.status, deleted_at: null });
        toast.success('User has been activated.', { className: 'fs-unmask' });
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
    setStatusMenuAnchor(null);
  };

  return (
    <div className={`${styles.reviewHeaderShell} ${className ?? ''}`}>
      <button type="button" className={styles.reviewBackButton} onClick={onBack ?? (() => navigate(-1))}>
        <IoChevronBack aria-hidden />
        Back
      </button>

      <header className={`${collectorShellStyles.collectorEditUserHeader} ${styles.reviewHeader}`}>
        <div className={collectorShellStyles.collectorEditUserHeaderIdentity}>
          <ProfileImage
            className={styles.headerProfileImage}
            reviewProfile
            id={userId}
            setCurrentAvatarURL={setCurrentAvatarURL}
            setAvatarFile={setAvatarFile}
            setAvatarPayload={setAvatarPayload}
            setAvatarAction={setAvatarAction}
            triggerProfileAvatar={triggerProfileAvatar}
            setImage={setImage}
            image={image}
            errors={errors}
            size="80px"
            disableDelete
            givingUserProfile={
              {
                first_name: thisUser.basic_info?.first_name || headerDisplayName.split(' ')[0] || '',
                last_name: thisUser.basic_info?.last_name || headerDisplayName.split(' ').slice(1).join(' ') || '',
              } as ProfileImageGivingUserProfile
            }
          />
          <div className={`${collectorShellStyles.collectorEditUserHeaderText} ${styles.reviewHeaderText}`}>
            <h1 className={`${collectorShellStyles.collectorEditUserHeaderName} ${styles.reviewHeaderName}`}>
              {headerDisplayName}
              {isDependent && <span className={styles.dependentLabel}> (dependent)</span>}
            </h1>
            <div className={`${collectorShellStyles.userStatusRow} ${styles.reviewStatusRow}`}>
              <span className={`${collectorShellStyles.userStatusLabel} ${styles.reviewStatusLabel}`}>
                User status:
              </span>
              <button
                type="button"
                className={`${collectorShellStyles.statusPillReadOnly} ${statusPillClass} ${styles.statusPillButton}`}
                aria-controls={statusMenuAnchor ? 'modern-review-user-status-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={statusMenuAnchor ? 'true' : undefined}
                onClick={(event) => setStatusMenuAnchor(event.currentTarget)}
              >
                {displayStatus}
                <IoChevronDown aria-hidden />
              </button>
              <Menu
                id="modern-review-user-status-menu"
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} onClick={() => handleStatusClick(status.id)}>
                    <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                      {status.name}
                    </ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </div>
            {isDependent && guardianName && thisUser.guardian_id && (
              <div className={`${collectorShellStyles.userStatusRow} ${styles.reviewStatusRow}`}>
                <span className={`${collectorShellStyles.userStatusLabel} ${styles.reviewStatusLabel}`}>Guardian:</span>
                <button
                  type="button"
                  className={styles.guardianLink}
                  onClick={() => navigate(`/review-profile/patient/${thisUser.guardian_id}`)}
                >
                  {guardianName}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={collectorShellStyles.collectorEditUserHeaderActions}>
          {canCreateCase && (
            <button
              type="button"
              className={collectorShellStyles.collectorEditUserCreateCaseLink}
              onClick={onCreateCase ?? (() => navigate('/review-profile/patient/select'))}
            >
              <IoAdd aria-hidden />
              Create new case
            </button>
          )}
          {thisUser.role.name !== UserRoleName.Dependent && (
            <>
              <IconButton
                aria-label="More actions"
                aria-controls={userHeaderMenuAnchor ? 'modern-review-user-header-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                onClick={(event) => setUserHeaderMenuAnchor(event.currentTarget)}
              >
                <IoEllipsisVertical />
              </IconButton>
              <Menu
                id="modern-review-user-header-menu"
                anchorEl={userHeaderMenuAnchor}
                open={Boolean(userHeaderMenuAnchor)}
                onClose={() => setUserHeaderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setUserHeaderMenuAnchor(null);
                    void inviteUser();
                  }}
                  disabled={
                    thisUser.role.name === UserRoleName.Dependent ||
                    ['Active', 'Pending', 'Deactivated'].includes(thisUser.status) ||
                    isInvitedUser
                  }
                >
                  <ListItemIcon>
                    <IoAdd />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                    Invite user
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setUserHeaderMenuAnchor(null);
                    setResetPasswordConfirmation({ isOpen: true, userState: 'Active' });
                  }}
                  disabled={['Deactivated', 'Not Invited', 'Invited'].includes(thisUser.status) || resetEmailSent}
                >
                  <ListItemIcon>
                    <HttpsOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                    Reset password
                  </ListItemText>
                </MenuItem>
                {showUnlockAccount && (
                  <MenuItem
                    onClick={() => {
                      setUserHeaderMenuAnchor(null);
                      openUnlockConfirm();
                    }}
                    disabled={!thisUser.email || isUnlocking}
                  >
                    <ListItemIcon>
                      <LockOpenOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                      Unlock account
                    </ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </div>
      </header>

      <Alert
        severity="info"
        className={addNewUserStyles.patientProfileNotice}
        sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
      >
        You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
      </Alert>

      <ConfirmationModal
        isOpen={unlockConfirmOpen}
        title="Unlock account"
        content={`Are you sure you want to unlock ${headerDisplayName}'s account? This clears the account-lockout state so they can sign in again.`}
        onClose={closeUnlockConfirm}
        onConfirm={() => void handleUnlockAccount()}
        isSubmitDisabled={isUnlocking}
      />

      {displayEmailModal && (
        <AddEmailModal
          isOpen={displayEmailModal}
          onClose={() => setDisplayEmailModal(false)}
          userId={userId}
          thisUser={thisUser}
          setThisUser={setThisUser}
          inviteUser={inviteUser}
        />
      )}
    </div>
  );
};

export default ModernReviewProfileHeader;
